import {
  Button,
  Dialog,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  DialogActions as MuiDialogActions,
  DialogContent as MuiDialogContent,
  DialogTitle as MuiDialogTitle,
  Select,
  TextField,
  Typography
} from '@material-ui/core';
import {
  createStyles,
  makeStyles,
  Theme,
  WithStyles,
  withStyles
} from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import moment from 'moment';
import PropTypes from 'prop-types';
import React from 'react';
import locale from '../locale/Localization';
import DatePicker from './DatePicker';

const styles = (theme: Theme) =>
  createStyles({
    root: {
      margin: 0,
      padding: theme.spacing(2)
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
      color: theme.palette.grey[500]
    }
  });

export interface DialogTitleProps extends WithStyles<typeof styles> {
  id: string;
  children: React.ReactNode;
  onClose: () => void;
}

const DialogTitle = withStyles(styles)((props: DialogTitleProps) => {
  const {children, classes, onClose, ...other} = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant='h6'>{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label='close'
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2)
  }
}))(MuiDialogContent);

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1)
  }
}))(MuiDialogActions);

const useStyles = makeStyles((theme: Theme) => ({
  formControl: {margin: theme.spacing(1), minWidth: 120, marginTop: '6%'},
  inputLabel: {width: '100%'},
  select: {width: 250, marginTop: 24},
  multiline: {width: 250, marginRight: theme.spacing(1), paddingTop: '4%'},
  dateField: {
    marginRight: theme.spacing(1),
    width: 250,
    marginTop: 4,
    marginLeft: 10
  }
}));

/**
 * Customized dialog for NotaryFunctions.
 *
 * @param {*} props - Dialog attributes passed as property.
 * @return {string} - The component content.
 */
// eslint-disable-next-line max-lines-per-function, require-jsdoc
export default function NotaryFunctionDialog(props) {
  const classes = useStyles();
  const [executionDate, setExecutionDate] = React.useState(null);
  const [signOffReason, setSignOffReason] = React.useState('');
  const [signOffComment, setSignOffComment] = React.useState('');

  /**
   * Validate the notary form fields.
   *
   * @return {boolean} - The validation result.
   */
  const validateFormFields = () => {
    if (props.showSignOff) {
      return executionDate && signOffReason && executionDate > props.minDate;
    } else {
      return executionDate && executionDate > props.minDate;
    }
  };

  /**
   * Handle date change for datepicker elements.
   *
   * @param {Date} date - Selected date from user.
   */
  const handleDateChange = async (date: Date) => {
    await setExecutionDate(moment(date).format(locale.dateFormat));
  };

  /**
   * Sub-render method for the execution date field.
   *
   * @return {string} - The date picker component.
   */
  const renderDateField = () => {
    return (
      <DatePicker
        id={'executionDate'}
        label={locale.executionDate}
        handler={handleDateChange}
        value={executionDate}
        required={true}
      />
    );
  };

  /**
   * Sub-render method for the function activate view.
   *
   * @return {string} - The function activate components.
   */
  const renderFunctionActivateFields = () => {
    return (
      <Grid container item xs={12} spacing={3} style={styles.gridContainer}>
        <Grid item xs={6}>
          {renderDateField()}
        </Grid>
      </Grid>
    );
  };

  /**
   * Sub-render method for the function deactivate view.
   *
   * @return {string} - The function deactivate components.
   */
  const renderFunctionDeactivateFields = () => {
    return (
      <Grid container item xs={12} spacing={3} style={styles.gridContainer}>
        <Grid item xs={6}>
          <FormControl required className={classes.formControl}>
            <InputLabel
              htmlFor='function-deactivate-reason'
              className={classes.inputLabel}
            >
              {locale.notaryFunctionDeactivateReasonLabel}
            </InputLabel>
            <Select
              native
              onChange={(event) => {
                setSignOffReason(event.target.value);
              }}
              className={classes.select}
              name='signOffReason'
              value={signOffReason}
              inputProps={{
                id: 'signOffReason'
              }}
            >
              <option value='' />
              <option value='ILLNESS'>
                {locale.notaryFunctionDeactivateReasonIllness}
              </option>
              <option value='SUSPENSION'>
                {locale.notaryFunctionDeactivateReasonSuspension}
              </option>
              <option value='RETIRED'>
                {locale.notaryFunctionDeactivateReasonRetired}
              </option>
            </Select>
          </FormControl>
          {renderDateField()}
        </Grid>
        <Grid item xs={6}>
          <TextField
            multiline
            rows='5'
            id='signOffComment'
            name='signOffComment'
            label={locale.comment}
            className={classes.multiline}
            value={signOffComment}
            onChange={(event) => {
              setSignOffComment(event.target.value);
            }}
          />
        </Grid>
      </Grid>
    );
  };

  return (
    <div>
      <Dialog
        disableBackdropClick
        aria-labelledby='customized-dialog-title'
        open={props.open}
        onClose={props.handleClose}
      >
        <DialogTitle id='customized-dialog-title' onClose={props.handleClose}>
          {props.showSignOff
            ? locale.notaryFunctionsDialogTitleDeactivate
            : locale.notaryFunctionsDialogTitleActivate}
        </DialogTitle>
        <DialogContent dividers>
          <Typography gutterBottom>
            {props.showSignOff
              ? locale.notaryFunctionDialogContentDeactivate
              : locale.notaryFunctionDialogContentActivate}
          </Typography>
          {props.showSignOff && renderFunctionDeactivateFields()}
          {!props.showSignOff && renderFunctionActivateFields()}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={props.handleClose} color='primary'>
            {locale.cancelButton}
          </Button>
          <Button
            autoFocus
            disabled={!validateFormFields()}
            onClick={() =>
              props.handleSubmit(executionDate, signOffReason, signOffComment)
            }
            color='primary'
          >
            {locale.confirmButton}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

NotaryFunctionDialog.propTypes = {
  showSignOff: PropTypes.bool,
  open: PropTypes.bool,
  handleOpen: PropTypes.func,
  handleClose: PropTypes.func,
  handleSubmit: PropTypes.func,
  minDate: PropTypes.func
};
